import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Solios custom engraving (replaces the Easify options block).
 *
 * Flow:
 *   1. "Add engraving" opens a popup with up to two lines of text + a "Confirm"
 *      button.
 *   2. Confirm stores the text, flips the trigger to an "Added" state, and
 *      injects a hidden `properties[_engraving_id]` input into the watch's
 *      add-to-cart form so the WATCH line carries the link id when added.
 *   3. Nothing is added to the cart yet. When the shopper adds the watch
 *      (CartAddEvent from the product form), we add the separate "Custom
 *      Engraving" product carrying the text + the SAME _engraving_id, then reset.
 *
 * Why the text rides on the engraving line (not the watch): Shopify's AJAX API
 * can't clear a line item's properties once set — you can only remove the whole
 * line. Keeping the text on the engraving line means "remove the engraving in
 * the cart" takes the text with it. The cart snippet renders that text under the
 * watch line (server-side, keyed on _engraving_id); engraving-cart.js removes an
 * orphaned engraving line if its watch is removed.
 *
 * @extends Component
 */
class EngravingOption extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.dialogComponent = this.querySelector('dialog-component');
    this.triggerLabel = this.querySelector('[data-engraving-trigger-label]');
    this.confirmButton = this.querySelector('[data-engraving-confirm]');
    this.removeButton = this.querySelector('[data-engraving-remove]');
    this.inputs = Array.from(this.querySelectorAll('[data-engraving-input]'));

    /** @type {{id: string, lines: Array<{label: string, value: string}>} | null} */
    this.pending = null;

    this.confirmButton?.addEventListener('click', this.#onConfirm);
    this.removeButton?.addEventListener('click', this.#onClear);
    // CartAddEvent shares the 'cartUpdate' event name, so this also fires for our
    // own refresh dispatch — guarded by `this.pending` + the source check below.
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);

    this.#syncPrice();
  }

  /**
   * Show the engraving's price in the buyer's active market currency. The Liquid
   * `price` is the shop's base currency; /products/{handle}.js returns the
   * presentment price for the current market (e.g. $25 in USD, not $30 CAD).
   */
  async #syncPrice() {
    const priceEl = this.querySelector('[data-engraving-price]');
    const handle = this.dataset.engHandle;
    if (!priceEl || !handle) return;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const product = await (await fetch(`${root}products/${handle}.js`)).json();
      const variant =
        product.variants.find((v) => String(v.id) === String(this.dataset.engVariant)) ||
        product.variants[0];
      if (variant?.price == null) return;
      const currency = window.Shopify?.currency?.active || 'CAD';
      const locale = document.documentElement.lang || 'en';
      priceEl.textContent = `+${new Intl.NumberFormat(locale, { style: 'currency', currency }).format(variant.price / 100)}`;
    } catch {
      // Keep the Liquid-rendered price.
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
  }

  /** The watch's add-to-cart form (this product's, falling back to any on the page). */
  get #form() {
    const scope = this.closest('.shopify-section') || document;
    return (
      scope.querySelector('product-form-component form[action*="/cart/add"]') ||
      document.querySelector('product-form-component form[action*="/cart/add"]')
    );
  }

  #onConfirm = () => {
    const lines = this.inputs
      .map((input) => ({ label: input.dataset.label || '', value: input.value.trim() }))
      .filter((line) => line.value !== '');

    // Nothing entered — treat Confirm as "no engraving".
    if (lines.length === 0) {
      this.#onClear();
      this.dialogComponent?.closeDialog();
      return;
    }

    const id = `eng-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    this.pending = { id, lines };

    // Tag the watch line so the cart can pair the two.
    this.#setFormInput('properties[_engraving_id]', id);
    this.#setState('added', lines);
    this.dialogComponent?.closeDialog();
  };

  #onClear = (event) => {
    event?.preventDefault?.();
    this.pending = null;
    this.#removeFormInput('properties[_engraving_id]');
    this.inputs.forEach((input) => (input.value = ''));
    this.#setState('idle');
  };

  #onCartAdd = async (event) => {
    if (!this.pending) return;
    // Only attach to the watch being added by the product form — not our own
    // refresh, a card add, or the Rebuy carousel.
    const source = event.detail?.data?.source;
    if (source && source !== 'product-form-component') return;

    const pending = this.pending;
    this.pending = null; // guard against the event firing twice
    this.#removeFormInput('properties[_engraving_id]');

    try {
      await this.#addEngraving(pending);
    } catch (error) {
      console.error('[engraving] failed to add the engraving product:', error);
    }
    this.#setState('idle');
    this.inputs.forEach((input) => (input.value = ''));
  };

  /** Add the separate "Custom Engraving" product, then refresh the cart/drawer. */
  async #addEngraving(pending) {
    const root = window.Shopify?.routes?.root || '/';
    const variantId = Number(this.dataset.engVariant);
    if (!variantId) return;

    const properties = { _engraving_id: pending.id, _engraving_line: '1' };
    if (this.dataset.productTitle) properties._engraving_for = this.dataset.productTitle;
    for (const line of pending.lines) {
      if (line.label) properties[line.label] = line.value;
    }

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties }] }),
    });
    if (!response.ok) throw new Error(await response.text());

    // No sections passed → cart-items re-renders the section from the server,
    // which pairs the engraving text under the watch line.
    const cart = await (await fetch(`${root}cart.js`)).json();
    document.dispatchEvent(new CartUpdateEvent(cart, 'engraving', { source: 'engraving' }));

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer?.hasAttribute('auto-open') && !drawer.refs?.dialog?.open) drawer.showDialog?.();
  }

  #setFormInput(name, value) {
    const form = this.#form;
    if (!form) return;
    let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  #removeFormInput(name) {
    this.#form?.querySelector(`input[name="${CSS.escape(name)}"]`)?.remove();
  }

  /** @param {'idle'|'added'} state */
  #setState(state, lines = []) {
    this.dataset.state = state;
    if (!this.triggerLabel) return;
    if (state === 'added') {
      this.triggerLabel.textContent = this.dataset.addedLabel || 'Engraving added';
      this.dataset.summary = lines.map((line) => line.value).join(' · ');
    } else {
      this.triggerLabel.textContent = this.dataset.defaultLabel || 'Add engraving';
      delete this.dataset.summary;
    }
  }
}

if (!customElements.get('engraving-option')) {
  customElements.define('engraving-option', EngravingOption);
}
